import { supabase } from '../lib/supabase';

/**
 * Real-Time Presence Service
 * Manages Supabase real-time subscriptions for partner presence, mood, and availability
 */

class RealtimePresenceService {
  constructor() {
    this.channels = new Map();
    this.listeners = new Map();
  }

  /**
   * Subscribe to partner's presence updates (status, connection quality, last seen)
   * @param {string} partnerId - Partner's user ID
   * @param {function} callback - Callback function to handle presence updates
   * @returns {object} Channel subscription
   */
  subscribeToPartnerPresence(partnerId, callback) {
    if (!partnerId) {
      console.warn('Partner ID required for presence subscription');
      return null;
    }

    const channelName = `partner_presence_${partnerId}`;
    
    // Remove existing channel if present
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'user_presence',
          filter: `user_id=eq.${partnerId}`
        },
        (payload) => {
          // Transform database payload to component-friendly format
          const presenceData = {
            status: payload?.new?.status || 'offline',
            connectionQuality: payload?.new?.connection_quality || 'good',
            currentMood: payload?.new?.current_mood || null,
            moodSyncedAt: payload?.new?.mood_synced_at || null,
            lastSeenAt: payload?.new?.last_seen_at || new Date()?.toISOString(),
            isActive: payload?.new?.is_active || false,
            updatedAt: payload?.new?.updated_at || new Date()?.toISOString(),
            eventType: payload?.eventType // INSERT, UPDATE, DELETE
          };

          callback(presenceData);
        }
      )?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`✅ Subscribed to partner presence: ${partnerId}`);
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`❌ Error subscribing to partner presence: ${partnerId}`);
        }
      });

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to partner's mood changes only
   * @param {string} partnerId - Partner's user ID
   * @param {function} callback - Callback function to handle mood updates
   * @returns {object} Channel subscription
   */
  subscribeToPartnerMood(partnerId, callback) {
    if (!partnerId) {
      console.warn('Partner ID required for mood subscription');
      return null;
    }

    const channelName = `partner_mood_${partnerId}`;
    
    // Remove existing channel if present
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_presence',
          filter: `user_id=eq.${partnerId}`
        },
        (payload) => {
          // Only trigger callback if mood actually changed
          const oldMood = payload?.old?.current_mood;
          const newMood = payload?.new?.current_mood;
          
          if (oldMood !== newMood) {
            const moodData = {
              currentMood: newMood,
              previousMood: oldMood,
              moodSyncedAt: payload?.new?.mood_synced_at || new Date()?.toISOString(),
              updatedAt: payload?.new?.updated_at || new Date()?.toISOString()
            };

            callback(moodData);
          }
        }
      )?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`✅ Subscribed to partner mood: ${partnerId}`);
        }
      });

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to partner's availability changes (is_active field)
   * @param {string} partnerId - Partner's user ID
   * @param {function} callback - Callback function to handle availability updates
   * @returns {object} Channel subscription
   */
  subscribeToPartnerAvailability(partnerId, callback) {
    if (!partnerId) {
      console.warn('Partner ID required for availability subscription');
      return null;
    }

    const channelName = `partner_availability_${partnerId}`;
    
    // Remove existing channel if present
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_presence',
          filter: `user_id=eq.${partnerId}`
        },
        (payload) => {
          // Only trigger callback if availability actually changed
          const oldAvailability = payload?.old?.is_active;
          const newAvailability = payload?.new?.is_active;
          
          if (oldAvailability !== newAvailability) {
            const availabilityData = {
              isActive: newAvailability,
              wasActive: oldAvailability,
              status: payload?.new?.status || 'offline',
              updatedAt: payload?.new?.updated_at || new Date()?.toISOString()
            };

            callback(availabilityData);
          }
        }
      )?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`✅ Subscribed to partner availability: ${partnerId}`);
        }
      });

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Fetch current partner presence data (initial load)
   * @param {string} partnerId - Partner's user ID
   * @returns {object} Current presence data or null
   */
  async fetchPartnerPresence(partnerId) {
    if (!partnerId) return null;

    try {
      const { data, error } = await supabase?.from('user_presence')?.select('*')?.eq('user_id', partnerId)?.maybeSingle();

      if (error) {
        console.error('Error fetching partner presence:', error?.message);
        return null;
      }

      if (!data) return null;

      // Transform to component-friendly format
      return {
        status: data?.status || 'offline',
        connectionQuality: data?.connection_quality || 'good',
        currentMood: data?.current_mood || null,
        moodSyncedAt: data?.mood_synced_at || null,
        lastSeenAt: data?.last_seen_at || new Date()?.toISOString(),
        isActive: data?.is_active || false,
        updatedAt: data?.updated_at || new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error fetching partner presence:', error);
      return null;
    }
  }

  /**
   * Update current user's presence data
   * @param {string} userId - Current user's ID
   * @param {object} updates - Presence updates (status, connection_quality, current_mood, is_active)
   * @returns {object} Updated presence data or error
   */
  async updateMyPresence(userId, updates) {
    if (!userId) {
      return { error: { message: 'User ID required' } };
    }

    try {
      const { data, error } = await supabase?.from('user_presence')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('user_id', userId)?.select()?.single();

      if (error) {
        console.error('Error updating presence:', error?.message);
        return { error };
      }

      return { data };
    } catch (error) {
      console.error('Error updating presence:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  }

  /**
   * Unsubscribe from a specific channel
   * @param {string} channelName - Channel name to unsubscribe
   */
  unsubscribe(channelName) {
    const channel = this.channels?.get(channelName);
    if (channel) {
      supabase?.removeChannel(channel);
      this.channels?.delete(channelName);
      console.log(`🔌 Unsubscribed from: ${channelName}`);
    }
  }

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    this.channels?.forEach((channel, channelName) => {
      supabase?.removeChannel(channel);
      console.log(`🔌 Unsubscribed from: ${channelName}`);
    });
    this.channels?.clear();
    this.listeners?.clear();
  }

  /**
   * Get time since last seen in human-readable format
   * @param {string} lastSeenAt - ISO timestamp
   * @returns {string} Human-readable time difference
   */
  getTimeSinceLastSeen(lastSeenAt) {
    if (!lastSeenAt) return 'Unknown';

    const now = new Date();
    const lastSeen = new Date(lastSeenAt);
    const diffMs = now - lastSeen;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
}

// Export singleton instance
const realtimePresenceService = new RealtimePresenceService();
export default realtimePresenceService;
